import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/Button";

const TIERS = [
  { key: "starter", name: "Priority", price: 500 },
  { key: "standard", name: "Full Team", price: 1500 },
  { key: "scale", name: "Full Department", price: 5000 },
];
const TIER_PRICE = { starter: 500, standard: 1500, scale: 5000 };
const NEXT = { starter: "standard", standard: "scale", scale: null };

const BOOSTS = [
  { key: "video", label: "Video boost", get: "4 more short-form videos every month", price: 149 },
  { key: "build", label: "Build boost", get: "2 more automation builds every month", price: 299 },
  { key: "agent", label: "Agent slot", get: "1 more AI agent from the roster", price: 99 },
  { key: "channel", label: "Channel boost", get: "1 more channel owned and managed", price: 199 },
];
const BOOST_MAP = Object.fromEntries(BOOSTS.map((b) => [b.key, b]));
const MAX_BOOSTS = 2;

export const PlanBuilder = () => {
  const [tier, setTier] = useState("starter");
  const [active, setActive] = useState([]); // boost keys

  const changeTier = (t) => {
    setTier(t);
    setActive([]);
  };

  const toggle = (key) => {
    setActive((cur) => {
      if (cur.includes(key)) return cur.filter((k) => k !== key);
      if (cur.length >= MAX_BOOSTS) return cur;
      return [...cur, key];
    });
  };

  const total = TIER_PRICE[tier] + active.reduce((s, k) => s + BOOST_MAP[k].price, 0);
  const tierName = TIERS.find((t) => t.key === tier).name;
  const items = [`${tierName} $${TIER_PRICE[tier].toLocaleString()}`, ...active.map((k) => `${BOOST_MAP[k].label.toLowerCase()} $${BOOST_MAP[k].price}`)];

  const next = NEXT[tier];
  const crossover = next && total >= TIER_PRICE[next];
  const nextName = next ? TIERS.find((t) => t.key === next).name : null;
  const boostsFull = active.length >= MAX_BOOSTS;
  const query = `?plan=${tier}${active.length ? `&boosts=${active.join(",")}` : ""}`;

  return (
    <Reveal>
      <div className="rounded-3xl border border-black/10 bg-[var(--bg-base)] p-6 md:p-8" data-testid="plan-builder">
        <p className="text-sm font-semibold uppercase tracking-[0.14em] text-[var(--ember)]">Build your plan</p>

        {/* Tier selector */}
        <div className="mt-5">
          <p className="mb-2 text-[13px] font-medium text-[var(--muted)]">Capacity level</p>
          <div className="inline-flex flex-wrap gap-1 rounded-full border border-black/10 p-1" role="group" aria-label="Capacity level">
            {TIERS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => changeTier(t.key)}
                aria-pressed={tier === t.key}
                data-testid={`plan-tier-${t.key}`}
                className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ember)] ${
                  tier === t.key ? "bg-[var(--mint)] text-[var(--charcoal)]" : "text-[var(--ink)]/70 hover:text-[var(--ink)]"
                }`}
              >
                {t.name} ${t.price.toLocaleString()}
              </button>
            ))}
          </div>
        </div>

        {/* Boosts */}
        {tier === "scale" ? (
          <p className="mt-8 rounded-2xl bg-[var(--bg-contrast)] p-5 text-[15px] leading-relaxed text-[var(--ink)]" data-testid="plan-scale-note">
            The Full Department tier includes the full department. Need more? Book a call for a custom scope.
          </p>
        ) : (
          <div className="mt-8 space-y-3" data-testid="plan-boosts">
            {BOOSTS.map((b) => {
              const on = active.includes(b.key);
              const disabled = !on && boostsFull;
              return (
                <div
                  key={b.key}
                  className={`flex items-center justify-between gap-4 rounded-2xl border p-4 transition-opacity ${
                    on ? "border-[var(--ember)]/40 bg-[var(--ember)]/[0.05]" : "border-black/10"
                  } ${disabled ? "opacity-45" : ""}`}
                >
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-[var(--ink)]">{b.label} · ${b.price}/mo</p>
                    <p className="text-[13px] text-[var(--muted)]">{b.get}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    disabled={disabled}
                    onClick={() => toggle(b.key)}
                    title={disabled ? "Two boosts max — consider the next tier." : undefined}
                    aria-label={`${b.label} ${on ? "on" : "off"}`}
                    data-testid={`plan-boost-${b.key}`}
                    className={`relative h-6 w-11 shrink-0 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ember)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-base)] ${
                      on ? "bg-[var(--ember)]" : "bg-black/15"
                    } ${disabled ? "cursor-not-allowed" : ""}`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Crossover callout */}
        {crossover && (
          <div className="mt-6 flex flex-col gap-3 rounded-2xl border border-[var(--ember)]/40 bg-[var(--ember)]/[0.06] p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="plan-crossover">
            <p className="text-[14px] leading-relaxed text-[var(--ink)]">
              At ${total.toLocaleString()}, {nextName} gets you more for less — more videos, more builds, more agents, faster turnaround.
            </p>
            <button
              type="button"
              onClick={() => changeTier(next)}
              data-testid="plan-switch"
              className="shrink-0 rounded-full bg-[var(--mint)] px-4 py-2 text-[13px] font-semibold text-[var(--charcoal)] transition-transform hover:-translate-y-0.5"
            >
              Switch to {nextName}
            </button>
          </div>
        )}

        {/* Live total */}
        <div className="mt-8 border-t border-black/10 pt-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[13px] font-medium text-[var(--muted)]">Your monthly plan</p>
              <p className="mt-1 text-[13px] text-[var(--muted)]" data-testid="plan-itemized">{items.join(" + ")}</p>
            </div>
            <p className="text-4xl font-semibold tracking-[-0.03em] text-[var(--ink)]" data-testid="plan-total">
              ${total.toLocaleString()}<span className="text-lg font-medium text-[var(--muted)]">/mo</span>
            </p>
          </div>
          <div className="mt-6">
            <Button to={`/contact${query}`} variant="primary" data-testid="plan-book-cta">
              Book a call with this plan
            </Button>
          </div>
        </div>
      </div>
    </Reveal>
  );
};
